import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDataEngine } from '@dhis2/app-runtime';
import { Button, Input, TextArea, SingleSelect, 
  SingleSelectOption, AlertBar, CircularLoader,
   Card, Modal, ModalTitle, ModalContent, ModalActions, IconSearch24
   } from '@dhis2/ui';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  shouldShowObjective,
  getCurrentUserWithRole,
} from './utils/userRoleUtils';
import { useNavigate } from "react-router-dom";

// Data Element Mappings for Tasks program stage
const TASK_DATAELEMENT_MAPPING = {
  selectedWeek: 'EiL3lPaL908',
  progressPercent: 'AElpNxzTdRh',
  status: 'LuTwfuN34pc',
  achievements: 'j9FdNkmPpXM',
  challenges: 'WSQ9nuXPnNL',
  nextSteps: 'M7PG2p2XsDA',
  objectiveId: 'plJovUlxfQ0',
  taskName: 'I0QtZkMbzxN',
  taskDescription: 'LR70eqTjB8m',
  kpiId: 'uXeH0X0aaCl',
  positionTei: 'SlgO60cMK3F',
  incumbentStaffId: 'OHKFI8GuxPs',
  subTasks: 'YBXgnhyue59'
};



const KPI_PROGRAM_STAGE_ID = 'ivyCLjNLFrd';
const KPI_PROGRAM_ID = 'yiT00XKrrHP';

const TASK_PROGRAM_STAGE_ID = 'Nv9nYNcBTdM';
const PROGRAM_ID = 'yiT00XKrrHP';

// Auto-save configuration
const AUTO_SAVE_DELAY = 3000; // 3 seconds
const DRAFT_NAMESPACE = 'weekly-progress-drafts';

const styleSheet = document.createElement("style");
styleSheet.innerText = `
@keyframes pulse-amber {
    0%, 100% { background-color: rgb(241, 142, 13); }
    50% { background-color: #f7ececff; }
  
  input[type="range"] {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    height: 8px;
    border-radius: 4px;
    background: #e9ecef;
    outline: none;
    width: 100%;
    margin: 10px 0;
    position: relative;
  }

  /* WebKit (Chrome, Safari) */
  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #6c757d;
    cursor: pointer;
    box-shadow: 0 4px 8px rgba(0,0,0,1);
    pointer-events: auto;
  }

  input[type="range"]:hover::-webkit-slider-thumb {
    background: #555;
  }

  /* Firefox */
  input[type="range"]::-moz-range-thumb {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #6c757d;
    cursor: pointer;
    border: none;
    box-shadow: 0 4px 8px rgba(0,0,0,1);
    pointer-events: auto;
  }

  input[type="range"]:hover::-moz-range-thumb {
    background: #555;
  }

  input[type="range"]::-moz-range-track {
    background: #e9ecef;
    border-radius: 4px;
    height: 8px;
  }
`;
document.head.appendChild(styleSheet);

// Draggable KPI Card Component
const DraggableKPICard = ({ kpi, isSelected, onSelect }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'kpi',
    item: { kpi },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  return (
    <div
      ref={drag}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
        marginBottom: '10px',
        marginTop: '10px',
        marginLeft: '10px',
        boxShadow: "0 8px 10px rgba(0, 0, 0, 0.3)",
        borderRadius: '14px',
        backgroundColor: 'rgb(230, 237, 152)'

      }}
      onClick={() => onSelect(kpi)}
    >
      <Card
        style={{
          border: isSelected ? '2px solid #e74c3c' : '1px solid #ddd',
          backgroundColor: isSelected ? 'rgb(242, 245, 206)' : 'white',
        }}
      >
        <div style={{ padding: '12px', borderRadius: '8px' }}>
          <h5 style={{ margin: '0 0 4px 0', color: '#2c3e50' }}>
            {kpi.name}
          </h5>
          {kpi.description && (
            <p style={{ margin: '0 0 4px 0', fontSize: '0.8rem', color: '#7f8c8d' }}>
              {kpi.description}
            </p>
          )}
          <div style={{ display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '0.7rem',
                        color: '#95a5a6' }}>
            <span>Target: {kpi.target || 'N/A'}</span>
          </div>
        </div>
      </Card>
    </div>
  );
};

// Update the TaskDetailModal component to show KPI
const TaskDetailModal = ({ task, statusOptions, onClose }) => {
  if (!task) return null;

  return (
    <Modal large onClose={onClose}>
      <ModalTitle>Task Progress Details</ModalTitle>
      <ModalContent>
        <div style={{ padding: '16px' }}>
          {/* Add KPI section */}
          {task.kpi && (
            <div style={{
              padding: '12px',
              backgroundColor: '#e8f4fd',
              borderRadius: '8px',
              marginBottom: '16px',
              borderLeft: '4px solid #2980b9',
              boxShadow: "0 8px 10px rgba(173, 157, 157, 1)",
            }}>
              <strong>Linked KPI:</strong>
              <div style={{ marginTop: '4px' }}>
                <strong>{task.kpi.name}</strong>
                {task.kpi.target && (
                  <span style={{ marginLeft: '8px', color: '#666' }}>
                    (Target: {task.kpi.target})
                  </span>
                )}
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <strong>Week Ending:</strong> {task.selectedWeek}
            </div>
            <div>
              <strong>Progress:</strong> {task.progressPercent}%
            </div>
            <div>
              <strong>Status:</strong> {statusOptions.find(s => s.value === task.status)?.label || task.status}
            </div>
          </div>

          {/* Rest of your existing modal content remains the same */}
          {task.taskName && (
            <div style={{ marginBottom: '16px' }}>
              <strong>Task Name:</strong>
              <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px', marginTop: '4px' }}>
                {task.taskName}
              </div>
            </div>
          )}
        </div>
      </ModalContent>
      <ModalActions>
        <Button onClick={onClose}>Close</Button>
      </ModalActions>
    </Modal>
  );
};


// Draggable Objective Card Component
const DraggableObjectiveCard = ({ objective, isSelected, onSelect, hasSubmittedTaskThisWeek, hasDraftThisWeek, isWeekLocked }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'objective',
    item: { objective },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  return (
    <div
      ref={drag}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
        marginBottom: '12px',
      }}
      onClick={() => onSelect(objective)}
    >
      <Card
        style={{
          border: isSelected ? '2px solid #08324eff' : '1px solid #ddd',
          backgroundColor: isSelected ? '#135385ff' : 'white',
          transition: 'all 0.2s ease',
          boxShadow: "0 4px 18px rgba(14, 13, 13, 1)",
        }}
      >
        <div style={{ padding: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#2c3e50' }}>
              {objective.name}
            </h4>
            <div style={{ display: 'flex', gap: '4px', flexDirection: 'column', alignItems: 'flex-end' }}>
              {hasSubmittedTaskThisWeek && isWeekLocked ? (
                <span style={{
                  padding: '2px 6px',
                  backgroundColor: '#ffc107',
                  color: '#000',
                  borderRadius: '8px',
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap'
                }}>
                  TASK EXISTS
                </span>
              ) : (hasSubmittedTaskThisWeek || hasDraftThisWeek) && !isWeekLocked ? (
                <span style={{
                  padding: '3px 6px',
                  backgroundColor: '#17a2b8',
                  color: '#fff',
                  borderRadius: '8px',
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  boxShadow: "0 8px 10px rgba(173, 157, 157, 1)",
                }}>
                  TOUCHED
                </span>
              ) : !isWeekLocked ? (
                // Show "NOT TOUCHED" when no task and no draft
                <span style={{
                  padding: '3px 6px',
                  color: '#fdfdfdff',
                  boxShadow: "0 8px 10px rgba(173, 157, 157, 1)",
                  borderRadius: '8px',
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  animation: 'pulse-amber 2s ease-in-out infinite'
                }}>
                  NOT TOUCHED
                </span>
              ) : null}
            </div>
          </div>
          <p style={{
            margin: '0 0 8px 0',
            color: '#7f8c8d',
            fontSize: '0.9rem',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}>
            {objective.description || 'No description available'}
          </p>
          <div style={{
            fontSize: '0.8rem',
            color: '#95a5a6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>{objective.orgUnitName}</span>
            <span style={{
              padding: '4px 8px',
              backgroundColor: isSelected ? '#2980b9' : '#ecf0f1',
              color: isSelected ? 'white' : '#7f8c8d',
              borderRadius: '12px',
              fontSize: '0.7rem'
            }}>
              Start Date:
              {objective.startDate ? new Date(objective.startDate).toLocaleDateString() : 'No date'}
            </span>
          </div>
        </div>
      </Card>
    </div>
  );
};

const ObjectiveSelectionDisplay = ({ selectedObjective, onClear, hasSubmittedTaskThisWeek }) => {
  return (
    <div
      style={{
        padding: '20px',
        border: `2px solid ${selectedObjective ? '#27ae60' : '#bdc3c7'}`,
        borderRadius: '8px',
        backgroundColor: selectedObjective ? '#e8f8ef' : '#f8f9fa',
        textAlign: 'center',
        marginBottom: '20px',
        transition: 'all 0.3s ease',
        minHeight: '40px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      {selectedObjective ? (
        <div style={{ textAlign: 'center' }}>
          <h4 style={{ margin: '0 0 8px 0', color: '#27ae60', fontSize: '1.3rem' }}>
            ✅ Objective Selected
          </h4>
          <p style={{ 
            margin: '0 0 16px 0', 
            fontWeight: 'bold',
            fontSize: '1.15rem',
            color: '#2c3e50'
          }}>
            {selectedObjective.name}
          </p>
          {hasSubmittedTaskThisWeek && (
            <div style={{
              padding: '10px',
              backgroundColor: '#fff8e1',
              borderRadius: '6px',
              marginBottom: '16px',
              border: '1px solid #ffc107',
              fontWeight: 500
            }}>
              📌 You already have tasks for this week
            </div>
          )}
          <Button 
            small 
            destructive 
            onClick={onClear}
            style={{ minWidth: '160px' }}
          >
            Change Objective
          </Button>
        </div>
      ) : (
        <div>
          <p style={{ 
            margin: '0 0 8px 0', 
            color: '#2c3e50', 
            fontWeight: 'bold',
            fontSize: '1.1rem'
          }}>
            Select an Objective Below
          </p>
          <p style={{ 
            margin: 0, 
            fontSize: '1rem', 
            color: '#7f8c8d',
            maxWidth: '300px'
          }}>
            Click any objective card to begin tracking weekly progress
          </p>
        </div>
      )}
    </div>
  );
};
// Auto-save Indicator Component
const AutoSaveIndicator = ({ isAutoSaving, hasUnsavedChanges, lastAutoSaved, isFormDisabled }) => {
  if (isFormDisabled) return null;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14 px',
      color: '#07611bff',
      marginBottom: '24px',
      padding: '8px',
      backgroundColor: '#c5c9cdff',
      borderRadius: '8px',
      border: '1px solid #e9ecef',
      boxShadow: "0 8px 10px rgba(192, 194, 192, 1)",
    }}>
      {isAutoSaving ? (
        <>
          <CircularLoader small />
          <span>Auto-saving your progress...</span>
        </>
      ) : hasUnsavedChanges ? (
        <>
          <span style={{ color: '#ffc107' }}>●</span>
          <span>Unsaved changes - auto-saving in {AUTO_SAVE_DELAY / 1000}s</span>
        </>
      ) : lastAutoSaved ? (
        <>
          <span style={{ color: '#28a745' }}>✓</span>
          <span>All changes saved {lastAutoSaved.toLocaleTimeString()}</span>
        </>
      ) : (
        <>
          <span style={{ color: '#6c757d' }}>ⓘ</span>
          <span>Start typing - your progress will be auto-saved</span>
        </>
      )}
    </div>
  );
};

// Helper: Get color based on progress percentage
const getProgressColor = (progress) => {
  if (progress >= 67) return '#28a745'; // Green
  if (progress >= 34) return '#ffc107'; // Amber/Yellow
  return '#dc3545'; // Red
};

const canAddNewTask = (weekDate) => {
  const now = new Date();
  const targetWeek = new Date(weekDate);
  const lockDate = new Date(targetWeek);
  lockDate.setDate(lockDate.getDate() + 7); // 7-day grace period
  return now <= lockDate;
};

// Check if week is in "view-only" mode (beyond 7 days)
const isViewOnlyWeek = (weekDate) => {
  return !canAddNewTask(weekDate);
};

// Check if the selected week is a PAST week (before current week)
const isPastWeek = (weekDate) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetWeek = new Date(weekDate);
  // Add 1 day to include the week ending date as current, only past after that
  const weekEndDate = new Date(targetWeek);
  weekEndDate.setDate(weekEndDate.getDate() + 1);
  return weekEndDate < today;
};

const getSliderFillColor = (progress) => {
  if (progress >= 67) return '#28a745'; // Green
  if (progress >= 34) return '#ffc107'; // Amber/Yellow
  return '#dc3545'; // Red
};

// SubTask Component (with lock icon)
const SubTask = ({ id, name, description, progressPercent, completed, index, onUpdate, onRemove, isFormDisabled }) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const isLocked = completed || isFormDisabled;

  const handleNameChange = ({ value }) => {
    if (!isLocked) onUpdate(index, { name: value });
  };
  const handleDescriptionChange = ({ value }) => {
    if (!isLocked) onUpdate(index, { description: value });
  };
  const handleProgressChange = (value) => {
    if (isLocked) return;
    const newProgress = Number(value);
    if (newProgress === 100) {
      setShowConfirmDialog(true);
    } else {
      onUpdate(index, { progressPercent: newProgress, completed: false });
    }
  };
  const confirmCompletion = () => {
    onUpdate(index, { progressPercent: 100, completed: true });
    setShowConfirmDialog(false);
  };
  const cancelCompletion = () => {
    setShowConfirmDialog(false);
  };

  const renderFormField = (label, children, required = true) => (
    <div style={{ marginBottom: '5px' }}>
      <div style={{ fontWeight: '400', marginBottom: '4px' }}>
        {label} {required && <span style={{ color: 'red' }}>*</span>}
      </div>
      {children}
    </div>
  );

  return (
    <>
      <div style={{
        padding: '12px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        marginBottom: '8px',
        backgroundColor: completed ? '#e8f5e9' : '#f9f9f9',
        opacity: isLocked ? 0.9 : 1,
        position: 'relative'
      }}>
        {/* Lock icon for completed subtasks */}
        {completed && (
          <div style={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            backgroundColor: '#28a745',
            color: 'white',
            borderRadius: '50%',
            width: '30px',
            height: '30px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '12px',
            fontWeight: 'bold',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}>
            🔒
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div style={{ flex: 1 }}>
            {renderFormField('Activity Description', (
              <TextArea
                value={description}
                onChange={handleDescriptionChange}
                placeholder="Describe this activity"
                rows={2}
                disabled={isLocked}
                required
              />
            ))}
          </div>
          {!completed && ( // Only show remove button if NOT completed
            <Button
              small
              destructive
              onClick={() => onRemove(index)}
              disabled={isFormDisabled}
              style={{ marginLeft: '8px' }}
            >
              Remove
            </Button>
          )}
        </div>
        <div style={{ marginTop: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{
              fontSize: '0.9rem',
              fontWeight: 'bold',
              color: getProgressColor(progressPercent),
              minWidth: '100px'
            }}>
              Progress: {progressPercent}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={progressPercent}
              onChange={(e) => handleProgressChange(e.target.value)}
              disabled={isLocked}
              style={{
                flex: 1,
                height: '12px',
                borderRadius: '6px',
                background: `linear-gradient(to right, ${getProgressColor(progressPercent)} 0%, ${getProgressColor(progressPercent)} ${progressPercent}%, #e9ecef ${progressPercent}%, #e9ecef 100%)`,
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none',
                outline: 'none'
              }}
            />
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <Modal small onClose={cancelCompletion}>
          <ModalTitle>Confirm Subtask Completion</ModalTitle>
          <ModalContent>
            <p>Are you sure this subtask is completed? This action cannot be undone.</p>
          </ModalContent>
          <ModalActions>
            <Button onClick={cancelCompletion}>Cancel</Button>
            <Button primary onClick={confirmCompletion}>Confirm Completion</Button>
          </ModalActions>
        </Modal>
      )}
    </>
  );
};


function WeeklyProgressTracker({ onSubmit, onCancel }) {
  const engine = useDataEngine();
  const [currentUser, setCurrentUser] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [objectives, setObjectives] = useState([]);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [existingTasks, setExistingTasks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSubmittedTaskThisWeek, setHasSubmittedTaskThisWeek] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [objectiveTasks, setObjectiveTasks] = useState({});
  const [statusOptions, setStatusOptions] = useState([]);
  const [objectiveDrafts, setObjectiveDrafts] = useState({});
  const [kpis, setKpis] = useState([]);
  const [showKPISelection, setShowKPISelection] = useState(false);

  const [kpiEnforcementActive, setKpiEnforcementActive] = useState(false);

  const navigate = useNavigate();

  const [allWeeks, setAllWeeks] = useState([]);

  // Auto-save state
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastAutoSaved, setLastAutoSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const autoSaveTimeoutRef = useRef(null);
  const notificationRef = useRef(null);
  const [showTaskForm, setShowTaskForm] = useState(false);

  const [currentStep, setCurrentStep] = useState('SELECT_OBJECTIVE'); // 'SELECT_OBJECTIVE' | 'SELECT_KPI' | 'FILL_TASK'
  const [selectedKPI, setSelectedKPI] = useState(null);

  // Week ending state (shared by all tasks)
  const [selectedWeek, setWeekEnding] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const daysUntilSunday = (7 - day) % 7;
    const sunday = new Date(today);
    sunday.setDate(today.getDate() + daysUntilSunday);
    return sunday.toISOString().substring(0, 10);
  });

  // Multiple tasks state
  const [tasks, setTasks] = useState([{
    id: Date.now().toString(),
    taskName: "",
    taskDescription: "",
    progressPercent: 0,
    status: "",
    achievements: "",
    challenges: "",
    nextSteps: "",
    kpiId: "",
    kpiName: "",
    isNew: true,
    eventId: "", // Track backend event ID
    subTasks: [] // Array of subtasks
  }]);

  const [selectedTaskIndex, setSelectedTaskIndex] = useState(0);
  const currentTask = tasks[selectedTaskIndex];
  const currentUserOrgUnitId = currentUser?.organisationUnits?.[0]?.id;


  const filteredObjectives = objectives.filter(obj =>
    obj.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    obj.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    obj.orgUnitName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const dateUtils = {
    // Safe date comparison that handles timezone and edge cases
    isDateAfter: (date1, date2) => {
      // Convert both to ISO strings and compare dates only (no time)
      const date1Str = new Date(date1).toISOString().split('T')[0];
      const date2Str = new Date(date2).toISOString().split('T')[0];

      return date1Str > date2Str;
    },

    // Check if we're on the same day
    isSameDay: (date1, date2) => {
      const date1Str = new Date(date1).toISOString().split('T')[0];
      const date2Str = new Date(date2).toISOString().split('T')[0];

      return date1Str === date2Str;
    },


    // Get end of day for a date
    getEndOfDay: (date) => {
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);
      return endOfDay;
    }
  };

  const fetchStatusOptions = async () => {
    try {
      const response = await engine.query({
        optionSet: {
          resource: 'optionSets/U8X3RbnJrYs',
          params: { fields: 'options[code,name]' }
        }
      });

      console.log('Valid status options:', response.optionSet.options);
      return response.optionSet.options;
    } catch (error) {
      console.error('Error fetching status options:', error);
      return [];
    }
  };

  const handleBackToObjective = () => {
    // Reset all task-related state
    setCurrentStep('SELECT_OBJECTIVE');
    setSelectedKPI(null);
    setTasks([{
        id: Date.now().toString(),
        taskName: "",
        taskDescription: "",
        progressPercent: 0,
        status: "",
        achievements: "",
        challenges: "",
        nextSteps: "",
        kpiId: "",
        kpiName: "",
        isNew: true,
        subTasks: []
    }]);
    setSelectedTaskIndex(0);
    setHasUnsavedChanges(false);
    setLastAutoSaved(null);
    setKpiEnforcementActive(false);
    
    // ✅ Reset week selection to current week
    const today = new Date();
    const day = today.getDay();
    const daysUntilSunday = (7 - day) % 7;
    const sunday = new Date(today);
    sunday.setDate(today.getDate() + daysUntilSunday);
    setWeekEnding(sunday.toISOString().substring(0, 10));
    
    // ✅ Clear the selected objective to force fresh selection
    // This will trigger the week selector to show all available weeks again
    setSelectedObjective(null);
    setHasSubmittedTaskThisWeek(false);
};


  const getAllWeeks = useCallback(() => {
    const weeks = new Set();

    // ✅ ALWAYS add current week
    weeks.add(selectedWeek);

    // Add all weeks from existing tasks
    existingTasks.forEach(task => {
      if (task.selectedWeek) {
        weeks.add(task.selectedWeek);
      }
    });

    // ✅ DEBUG: Log what weeks we found
    console.log('📊 getAllWeeks result:', {
      currentWeek: selectedWeek,
      existingTasksCount: existingTasks.length,
      totalWeeks: Array.from(weeks).length,
      weeks: Array.from(weeks)
    });

    // Sort weeks in descending order (most recent first)
    return Array.from(weeks).sort((a, b) => new Date(b) - new Date(a));
  }, [existingTasks, selectedWeek]);

  // Update allWeeks when existing tasks change
  useEffect(() => {
    const weeks = getAllWeeks();
    setAllWeeks(weeks);
  }, [getAllWeeks]);

  // Auto-show form when NO existing tasks exist (new week)
    useEffect(() => {
      if (!hasSubmittedTaskThisWeek) {
        setShowTaskForm(true);
      }
    }, [hasSubmittedTaskThisWeek]);

  // Function to check if a specific week is locked
  const isSpecificWeekLocked = (weekDate) => {
    const now = new Date();
    const targetWeek = new Date(weekDate);

    // Add 7 days grace period
    const lockDate = new Date(targetWeek);
    lockDate.setDate(lockDate.getDate() + 7);

    return now > lockDate;
  };

  const checkObjectiveHasDraft = async (objectiveId, selectedWeek) => {
    try {
      const draftId = getDraftId(objectiveId, selectedWeek);
      const response = await engine.query({
        draft: {
          resource: `dataStore/${DRAFT_NAMESPACE}/${draftId}`,
        }
      });
      return !!response.draft;
    } catch (error) {
      return false;
    }
  };


  const fetchKPIsForObjective = async (objectiveId) => {
    try {
      // Fetch KPIs that are linked to the same objective
      const response = await engine.query({
        kpis: {
          resource: 'events',
          params: {
            program: KPI_PROGRAM_ID,
            programStage: KPI_PROGRAM_STAGE_ID,
            filter: [`plJovUlxfQ0:eq:${objectiveId}`],
            fields: 'event,eventDate,orgUnitName,dataValues[dataElement,value]',
            pageSize: 1000
          }
        }
      });

      console.log('Raw KPI response:', response);

      // Process KPI data
      const processedKPIs = response.kpis?.events?.map(event => {
        const dataValues = event.dataValues || [];

        return {
          id: event.event, // Use event ID as KPI identifier
          eventId: event.event,
          name: dataValues.find(dv => dv.dataElement === 'DBmylDQA9KX')?.value || 'Unnamed KPI',
          description: dataValues.find(dv => dv.dataElement === 'rQqF75V9t7K')?.value || '',
          target: dataValues.find(dv => dv.dataElement === 'Ru68Ax2Xo2L')?.value || 'N/A',
          objectiveId: dataValues.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value || '',
          orgUnitName: event.orgUnitName,
          eventDate: event.eventDate
        };
      }) || [];

      console.log('Processed KPIs:', processedKPIs);
      setKpis(processedKPIs);

      return processedKPIs;
    } catch (error) {
      console.error('Error fetching KPIs:', error);
      showNotification('Failed to load KPIs for this objective', 'error');
      setKpis([]);
      return [];
    }
  };


  const handleKPISelect = (kpi) => {
    console.log('🎯 KPI Selected - Before update:', {
      taskName: currentTask.taskName,
      hasSubmittedTaskThisWeek: hasSubmittedTaskThisWeek,
      willAutoSave: !!selectedObjective
    });
    updateCurrentTask({
      kpiId: kpi.eventId,
      kpiName: kpi.name
    });

    // Disable KPI enforcement once KPI is selected
    if (kpiEnforcementActive) {
      setKpiEnforcementActive(false);
      showNotification("KPI linked successfully! You can now fill other task details.", "success");
    }
  };

  const shouldDisableField = () => {
    return kpiEnforcementActive && !currentTask.kpiId;
  };

  const handleKPIChange = (kpi) => {
    if (kpi) {
      handleKPISelect(kpi);
    } else {
      updateCurrentTask({
        kpiId: "",
        kpiName: ""
      });
    };
  };


  // Enhanced notification system
  const showNotification = (message, type = 'error') => {
    if (window.notificationTimeout) {
      clearTimeout(window.notificationTimeout);
    }

    notificationRef.current = { message, type, timestamp: Date.now() };
    setNotification({ message, type });

    window.notificationTimeout = setTimeout(() => {
      if (notificationRef.current?.message === message) {
        setNotification(null);
        notificationRef.current = null;
      }
      window.notificationTimeout = null;
    }, 8000);
  };

  // Update the isWeekLocked function
  const isWeekLocked = (weekEndingDate, task) => {
    try {
      const now = new Date();
      const selectedWeek = new Date(weekEndingDate);
      const lockDate = new Date(selectedWeek);
      lockDate.setDate(lockDate.getDate() + 7); // Allow editing for 7 days

      // Return true if past the lock date OR task is 100% complete
      return now > lockDate || task?.progressPercent === 100;
    } catch (error) {
      console.error('Error in week locking logic:', error);
      return false;
    }
  };


  // Update this to allow editing existing tasks in past weeks
  // Disable form only when task is 100% complete
  const isFormDisabled = currentTask?.progressPercent === 100;

  // Allow editing existing tasks even in past weeks
  const canEditTask = !isFormDisabled;

  // Check if user can add a new task (only in current week, not past weeks)
  const canAddNewTaskForWeek = !isViewOnlyWeek(selectedWeek) && !hasSubmittedTaskThisWeek;

  // Check if we're viewing a past week with existing tasks
  const isViewingPastWeekWithExistingTask = isViewOnlyWeek(selectedWeek) && hasSubmittedTaskThisWeek;

  // Generate draft ID for dataStore
  const getDraftId = (objectiveId, selectedWeek) => {
    return `${DRAFT_NAMESPACE}-${objectiveId}-${selectedWeek}`;
  };

  const syncTasksToDatabase = async (tasksData, objective, selectedWeek) => {
    try {
      // ✅ NEW: Filter out tasks with invalid names BEFORE syncing
      const validTasks = tasksData.filter(task =>
        task.taskName && task.taskName.trim() !== '' && task.taskName !== 'Unnamed Task'
      );

      if (validTasks.length === 0) {
        console.log('⏸️ No valid tasks to sync to database');
        return { successCount: 0, errorCount: 0 };
      }

      // Get existing events for this objective and week to avoid duplicates
      const existingEvents = await getExistingEvents(objective.id, selectedWeek);

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      // Process each VALID task
      for (const task of validTasks) {
        console.log('🔍 Syncing valid task:', {
          taskName: task.taskName,
          hasKpiId: !!task.kpiId,
          hasStatus: !!task.status,
          progress: task.progressPercent
        });

        try {
          await createOrUpdateTaskEvent(task, objective, selectedWeek, existingEvents);
          successCount++;
          console.log(`✅ Synced task: "${task.taskName}"`);
        } catch (error) {
          console.error(`❌ Failed to sync task "${task.taskName}":`, error);
          errorCount++;
          errors.push(error.message);
        }
      }

      console.log(`📊 Sync Results: ${successCount} successful, ${errorCount} failed`);

      if (errorCount > 0) {
        console.warn(`Some tasks failed to sync: ${errors.join('; ')}`);
      }

      // Verify creation if we had any successes
      if (successCount > 0) {
        try {
          await verifyTaskCreation(objective.id, validTasks[0].taskName);
        } catch (verifyError) {
          console.warn('Verification failed but continuing:', verifyError);
        }
      }

      console.log('✅ Auto-save completed - tasks synced to the databaaw');
      return { successCount, errorCount };

    } catch (error) {
      console.error('❌ Error syncing tasks to database:', error);
      return { successCount: 0, errorCount: tasksData.length };
    }
  };


  const createOrUpdateTaskEvent = async (task, objective, selectedWeek, existingEvents) => {
    const objectivePositionTei = objective.trackedEntityInstance;

    try {
      // 1. Prepare data values
      const dataValues = prepareDataValues(task, objective, selectedWeek, objectivePositionTei);

      // 2. Check if event exists
      const existingEvent = findExistingEvent(task, existingEvents);

      if (existingEvent) {
        return await updateExistingEvent(existingEvent.event, dataValues, task);
      } else {
        return await createNewEvent(objective, dataValues, objectivePositionTei, task);
      }

    } catch (error) {
      handleTaskError(error, task);
      throw error; // Re-throw to let caller handle it
    }
  };


  // Helper function 1: Prepare data values
  const prepareDataValues = (task, objective, selectedWeek, objectivePositionTei) => {
    const dataValues = [
      { dataElement: TASK_DATAELEMENT_MAPPING.selectedWeek, value: selectedWeek },
      { dataElement: TASK_DATAELEMENT_MAPPING.progressPercent, value: task.progressPercent?.toString() || '0' },
      { dataElement: TASK_DATAELEMENT_MAPPING.status, value: task.status || 'pending' },
      { dataElement: TASK_DATAELEMENT_MAPPING.achievements, value: task.achievements || '' },
      { dataElement: TASK_DATAELEMENT_MAPPING.challenges, value: task.challenges || '' },
      { dataElement: TASK_DATAELEMENT_MAPPING.nextSteps, value: task.nextSteps || '' },
      { dataElement: TASK_DATAELEMENT_MAPPING.objectiveId, value: objective.id },
      { dataElement: TASK_DATAELEMENT_MAPPING.taskName, value: task.taskName || '' },
      { dataElement: TASK_DATAELEMENT_MAPPING.taskDescription, value: task.taskDescription || '' },
      { dataElement: TASK_DATAELEMENT_MAPPING.kpiId, value: task.kpiId || '' },
      { dataElement: TASK_DATAELEMENT_MAPPING.positionTei, value: objectivePositionTei },
      { dataElement: TASK_DATAELEMENT_MAPPING.subTasks, value: JSON.stringify(task.subTasks || []) }
    ].filter(dv => dv.value !== undefined && dv.value !== null);

    console.log('🔍 Task data prepared:', {
      taskName: task.taskName || 'Unnamed Task',
      dataValuesCount: dataValues.length,
      hasKPI: !!task.kpiId,
      subTasksCount: task.subTasks?.length || 0
    });

    return dataValues;
  };

  // Helper function 2: Find existing event
  const findExistingEvent = (task, existingEvents) => {
    const existingEvent = existingEvents.find(event => {
      const taskNameValue = event.dataValues?.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.taskName)?.value;
      return taskNameValue === (task.taskName || '');
    });

    if (existingEvent) {
      console.log('🔄 Found existing event:', existingEvent.event);
    }

    return existingEvent;
  };

  // Helper function 3: Update existing event
  const updateExistingEvent = async (eventId, dataValues, task) => {
    try {
      console.log('🔄 Attempting to update event:', eventId);
      console.log('📤 Update dataValues:', dataValues);

      const result = await engine.mutate({
        resource: `events/${eventId}`,
        type: 'update',
        data: {
          program: PROGRAM_ID,
          programStage: TASK_PROGRAM_STAGE_ID,
          orgUnit: currentUserOrgUnitId,
          dataValues
        }
      });

      console.log(`✅ Successfully updated event for task: "${task.taskName || 'Unnamed Task'}"`);
      return { success: true, action: 'updated', eventId };

    } catch (error) {
      console.error(`❌ Failed to update event ${eventId} for task: "${task.taskName || 'Unnamed Task'}"`);

      // Enhanced error logging for updates
      if (error.details) {
        console.error('❌ Update error details:', {
          message: error.message,
          httpStatus: error.httpStatus,
          httpStatusCode: error.httpStatusCode
        });

        if (error.details.response) {
          console.error('❌ Update response:', error.details.response);

          if (error.details.response.importSummaries) {
            const importSummary = error.details.response.importSummaries[0];
            console.error('❌ Update import summary:', {
              status: importSummary?.status,
              description: importSummary?.description,
              conflicts: importSummary?.conflicts
            });
          }
        }
      }

      throw new Error(`Failed to update task "${task.taskName || 'Unnamed Task'}": ${error.message}`);
    }
  };

  // Helper function 4: Create new event
  const createNewEvent = async (objective, dataValues, objectivePositionTei, task) => {
    console.log('🎯 Creating task for objective:', objective.id, 'with position TEI:', objective.trackedEntityInstance);
    try {

      const enrollment = objective.enrollment
      const eventData = {
        program: PROGRAM_ID,
        programStage: TASK_PROGRAM_STAGE_ID,
        orgUnit: currentUserOrgUnitId || objective.orgUnit,
        eventDate: new Date().toISOString().split('T')[0],
        status: 'ACTIVE',
        trackedEntityInstance: objectivePositionTei,
        enrollment: enrollment,
        dataValues: dataValues
      };

      console.log('🚀 Creating new event with payload:', {
        orgUnit: currentUserOrgUnitId || objective.orgUnit,
        trackedEntityInstance: objectivePositionTei,
        dataValuesCount: dataValues.length
      });

      const result = await engine.mutate({
        resource: 'events',
        type: 'create',
        data: eventData
      });

      console.log(`✅ Successfully created event for task: "${task.taskName || 'Unnamed Task'}"`);
      return { success: true, action: 'created', result };

    } catch (error) {
      console.error(`❌ Failed to create event for task: "${task.taskName || 'Unnamed Task'}"`);

      // Handle warnings as success (events that are created but with warnings)
      if (error.details?.response?.importSummaries?.[0]?.importCount?.imported === 1) {
        console.log(`⚠️ Event created with warnings for task: "${task.taskName || 'Unnamed Task'}"`);
        return { success: true, action: 'created_with_warnings', warnings: true };
      }

      // Enhanced error logging for creation
      if (error.details?.response) {
        const importSummary = error.details.response.importSummaries?.[0];
        console.error('❌ Creation error details:', {
          imported: importSummary?.importCount?.imported,
          updated: importSummary?.importCount?.updated,
          status: importSummary?.status,
          description: importSummary?.description,
          conflicts: importSummary?.conflicts
        });
      }

      throw new Error(`Failed to create task "${task.taskName || 'Unnamed Task'}": ${error.message}`);
    }
  };

  // Helper function 5: Error handler
  const handleTaskError = (error, task) => {
    console.error(`💥 Critical error for task: "${task.taskName || 'Unnamed Task'}"`, {
      errorMessage: error.message,
      taskName: task.taskName,
      hasKPI: !!task.kpiId,
      progress: task.progressPercent
    });
  };

  // Get existing events for this objective and week
  const getExistingEvents = async (objectiveId, selectedWeek) => {
    try {
      const response = await engine.query({
        events: {
          resource: 'events',
          params: {
            program: PROGRAM_ID,
            programStage: TASK_PROGRAM_STAGE_ID,
            filter: [
              `plJovUlxfQ0:eq:${objectiveId}`,
              `${TASK_DATAELEMENT_MAPPING.selectedWeek}:eq:${selectedWeek}`
            ],
            fields: 'event,dataValues[dataElement,value]',
            pageSize: 100
          }
        }
      });

      return response.events?.events || [];
    } catch (error) {
      console.error('Error fetching existing events:', error);
      return [];
    }
  };

  const saveDraft = async (tasksData, objective, selectedWeek) => {
    if (!objective || isWeekLocked(selectedWeek)) return;

    // ✅ NEW: Filter out tasks with empty names
    const validTasks = tasksData.filter(task =>
      task.taskName && task.taskName.trim() !== '' && task.taskName !== ''
    );

    // ✅ NEW: If no valid tasks, don't save
    if (validTasks.length === 0) {
      console.log('⏸️ Skipping auto-save: No task with a valid name');
      return;
    }

    try {
      setIsAutoSaving(true);

      // First, save to dataStore as backup (always do this)
      const draftId = getDraftId(objective.id, selectedWeek);
      const draftData = {
        tasks: validTasks, 
        objective,
        lastSaved: new Date().toISOString(),
        selectedWeek
      };

      await engine.mutate({
        resource: `dataStore/${DRAFT_NAMESPACE}/${draftId}`,
        type: 'update',
        data: draftData,
      });

      try {
        const result = await syncTasksToDatabase(validTasks, objective, selectedWeek); 

        setLastAutoSaved(new Date());
        setHasUnsavedChanges(false);

        setObjectiveDrafts(prev => ({
          ...prev,
          [objective.id]: true
        }));

        // Show success notification
        if (result.successCount > 0) {
        // Actual tasks synced to database
        showNotification(`Progress saved! ${result.successCount} task(s) synced.`, 'success');
      } else if (validTasks.length > 0 && result.errorCount === 0) {
        // Tasks were VALID but intentionally skipped (e.g., "Unnamed Task" filtered out)
        showNotification('✅ Draft saved. Add a task name to sync to database.', 'success');
      } else if (result.errorCount > 0) {
        // Actual sync failures (network errors, validation issues)
        showNotification(`⚠️ ${result.errorCount} task(s) failed to sync. Progress saved to draft.`, 'warning');
      } else {
        showNotification('Progress saved to draft.', 'success');
      }

      } catch (syncError) {
        console.error('❌ database sync failed, but draft saved locally:', syncError);
        setLastAutoSaved(new Date());
        setHasUnsavedChanges(false);
        showNotification('Progress saved locally.', 'success');
      }

    } catch (error) {
      console.error('❌ Auto-save failed:', error);
      showNotification('Failed to save progress', 'error');
    } finally {
      setIsAutoSaving(false);
    }
  }

  // Updated   loadDraft to handle multiple tasks
  const loadDraft = async (objectiveId, selectedWeek) => {
    try {
      const draftId = getDraftId(objectiveId, selectedWeek);
      const response = await engine.query({
        draft: {
          resource: `dataStore/${DRAFT_NAMESPACE}/${draftId}`,
        }
      });

      if (response.draft && response.draft.tasks && Array.isArray(response.draft.tasks)) {
        // Validate that tasks have the expected structure
        const validTasks = response.draft.tasks.map(task => ({
          id: task.id || Date.now().toString(),
          taskName: task.taskName || "",
          taskDescription: task.taskDescription || "",
          progressPercent: task.progressPercent || 0,
          status: task.status || "",
          achievements: task.achievements || "",
          challenges: task.challenges || "",
          nextSteps: task.nextSteps || "",
          kpiId: task.kpiId || "",
          kpiName: task.kpiName || "",
          isNew: task.isNew !== undefined ? task.isNew : true,
          eventId: task.eventId || "",
          subTasks: task.subTasks || []
        }));

        setTasks(validTasks);
        setLastAutoSaved(new Date(response.draft.lastSaved));
        setHasUnsavedChanges(false);
        showNotification('Loaded auto-saved tasks from your previous session', 'success');
        return true;
      }
    } catch (error) {
      // ✅ EXPECTED: No draft exists yet, this is normal
      console.log('No draft found for this objective/week - this is normal');
    }
    return false;
  };

  // Delete draft (when form is submitted or cleared)
  const deleteDraft = async (objectiveId, selectedWeek) => {
    try {
      const draftId = getDraftId(objectiveId, selectedWeek);
      await engine.mutate({
        resource: `dataStore/${DRAFT_NAMESPACE}/${draftId}`,
        type: 'delete',
      });
    } catch (error) {
      console.log('No draft to delete or delete failed');
    }
  };

  // Check if a task already exists for this week
  const checkExistingTaskThisWeek = async (objectiveId) => {
    try {
      const response = await engine.query({
        tasks: {
          resource: 'events',
          params: {
            program: PROGRAM_ID,
            programStage: TASK_PROGRAM_STAGE_ID,
            filter: [
              `plJovUlxfQ0:eq:${objectiveId}`,
              `${TASK_DATAELEMENT_MAPPING.selectedWeek}:eq:${selectedWeek}`
            ],
            fields: 'event',
            pageSize: 1
          }
        }
      });
      return response.tasks?.events?.length > 0;
    } catch (error) {
      console.error('Error checking existing tasks:', error);
      return false;
    }
  };


  // Update auto-save to allow editing past weeks
  useEffect(() => {
    const hasValidTask = tasks.some(task =>
      task.taskName && task.taskName.trim() !== '' && task.taskName !== 'Unnamed Task'
    );

    if (hasValidTask && hasUnsavedChanges && selectedObjective && !isFormDisabled) {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      autoSaveTimeoutRef.current = setTimeout(() => {
        saveDraft(tasks, selectedObjective, selectedWeek);
      }, AUTO_SAVE_DELAY);

      return () => {
        if (autoSaveTimeoutRef.current) {
          clearTimeout(autoSaveTimeoutRef.current);
        }
      };
    }
  }, [tasks, hasUnsavedChanges, selectedObjective, selectedWeek, isFormDisabled]);



  // Calculate task progress from subtasks
  const calculateTaskProgress = (subTasks) => {
    if (!subTasks || subTasks.length === 0) return 0;
    const totalProgress = subTasks.reduce((sum, subTask) => sum + (subTask.progressPercent || 0), 0);
    return Math.round(totalProgress / subTasks.length);
  };


  const handleSearchChange = ({ value }) => {
    setSearchTerm(value);
  };



  const debugPositionTEIAssignment = (tasks, objective) => {
    console.log('🔍 POSITION TEI DEBUG - Task Creation:');
    console.log('Objective:', {
      id: objective.id,
      name: objective.name,
      trackedEntityInstance: objective.trackedEntityInstance,
      hasPositionTEI: !!objective.trackedEntityInstance
    });

    tasks.forEach((task, index) => {
      console.log(`Task ${index + 1}: "${task.taskName}"`, {
        willGetPositionTEI: objective.trackedEntityInstance,
        hasRequiredData: !!(task.taskName && task.kpiId && task.status)
      });
    });
  };

  // Add this debug function to verify task creation
  const verifyTaskCreation = async (objectiveId, taskName) => {
    try {
      const response = await fetch(`/api/events.json?program=yiT00XKrrHP&programStage=Nv9nYNcBTdM&filter=plJovUlxfQ0:eq:${objectiveId}&fields=event,dataValues[dataElement,value]`);
      const data = await response.json();

      const newTask = data.events?.find(event => {
        const taskNameValue = event.dataValues.find(dv => dv.dataElement === 'I0QtZkMbzxN')?.value;
        return taskNameValue === taskName;
      });

      if (newTask) {
        const positionTei = newTask.dataValues.find(dv => dv.dataElement === 'SlgO60cMK3F')?.value;
        console.log('✅ VERIFIED: New task created with Position TEI:', {
          taskName: taskName,
          positionTei: positionTei,
          hasPositionTei: !!positionTei
        });
      } else {
        console.log('❌ TASK NOT FOUND: Task may not have been created');
      }
    } catch (error) {
      console.error('Error verifying task creation:', error);
    }
  };


  // Update the objective selection
  const handleObjectiveSelect = async (objective) => {
    setSelectedObjective(objective);

    debugPositionTEIAssignment(tasks, objective);

    // Check for existing submitted task
    const alreadyExists = await checkExistingTaskThisWeek(objective.id);
    setHasSubmittedTaskThisWeek(alreadyExists);

    if (alreadyExists) {
      showNotification("This objective already has a task for the selected week. Viewing mode only.", "warning");
      await loadExistingTasksForWeek(objective.id);
      setKpiEnforcementActive(false);
    } else {
      // Try to load draft data
      const hasDraft = await loadDraft(objective.id, selectedWeek);
      if (!hasDraft && !isInitialLoad) {
        // Reset tasks if no draft and no existing task
        setTasks([{
          id: Date.now().toString(),
          taskName: "",
          taskDescription: "",
          progressPercent: 0,
          status: "",
          achievements: "",
          challenges: "",
          nextSteps: "",
          kpiId: "",
          kpiName: "",
          isNew: true,
          subTasks: []
        }]);
        setSelectedTaskIndex(0);
      }
      setIsInitialLoad(false);

      // ENFORCE KPI: Show notification and activate enforcement
      setKpiEnforcementActive(true);
      showNotification("Please select KPI before filling task details", "warning");
    }

    // Fetch KPIs for this objective
    await fetchKPIsForObjective(objective.id);

    // Update the draft status for this objective
    const hasDraft = !alreadyExists && await checkObjectiveHasDraft(objective.id, selectedWeek);
    setObjectiveDrafts(prev => ({
      ...prev,
      [objective.id]: hasDraft
    }));
  };


  // Load ALL tasks for a specific week
  const loadExistingTasksForWeek = async (objectiveId, weekDate = selectedWeek) => {
    try {
      console.log(`🔍 Loading tasks for objective ${objectiveId}, week ${weekDate}`);

      const response = await engine.query({
        tasks: {
          resource: 'events',
          params: {
            program: PROGRAM_ID,
            programStage: TASK_PROGRAM_STAGE_ID,
            filter: [
              `plJovUlxfQ0:eq:${objectiveId}`,
              `${TASK_DATAELEMENT_MAPPING.selectedWeek}:eq:${weekDate}`
            ],
            fields: 'event,eventDate,orgUnitName,dataValues[dataElement,value]',
            pageSize: 1000, // ✅ Load ALL tasks, not just 1
            order: 'lastUpdated:desc'
          }
        }
      });

      if (response.tasks?.events?.length > 0) {
        console.log(`✅ Found ${response.tasks.events.length} task(s) for week ${weekDate}`);

        const tasksData = response.tasks.events.map(event => {
          const dataValues = event.dataValues || [];

          // Parse subtasks
          const subTasksValue = dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.subTasks)?.value;
          let subTasks = [];
          try {
            subTasks = subTasksValue ? JSON.parse(subTasksValue) : [];
          } catch (error) {
            console.warn('Failed to parse subtasks JSON:', error);
            subTasks = [];
          }

          // Find linked KPI
          const kpiId = dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.kpiId)?.value || '';
          const kpi = kpis.find(k => k.eventId === kpiId);

          return {
            id: event.event,
            eventId: event.event,
            taskName: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.taskName)?.value || 'Unnamed Task',
            taskDescription: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.taskDescription)?.value || '',
            progressPercent: Number(dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.progressPercent)?.value) || 0,
            status: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.status)?.value || '',
            achievements: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.achievements)?.value || '',
            challenges: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.challenges)?.value || '',
            nextSteps: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.nextSteps)?.value || '',
            kpiId: kpiId,
            kpiName: kpi?.name || '',
            subTasks: subTasks,
            isNew: false
          };
        });

        let initialIndex = 0;
          const taskWithActivities = tasksData.findIndex(t => 
            t.subTasks && t.subTasks.length > 0
          );
          if (taskWithActivities !== -1) {
            initialIndex = taskWithActivities;
            console.log(`✅ Auto-selected task ${initialIndex + 1} (has ${tasksData[initialIndex].subTasks.length} activities)`);
          } else {
            console.log('⚠️ No tasks have activities - showing first task');
          }

          setTasks(tasksData);
          setSelectedTaskIndex(initialIndex);
          setHasSubmittedTaskThisWeek(true);
          setShowTaskForm(false);
          showNotification(
            `Loaded ${tasksData.length} task(s) from ${weekDate}. Selected task with activities.`,
            'success'
         );

      } else {
        console.log(`ℹ️ No tasks found for week ${weekDate}, creating new task form`);

        // No tasks exist for this week - create new task
        setTasks([{
          id: Date.now().toString(),
          taskName: '',
          taskDescription: '',
          progressPercent: 0,
          status: '',
          achievements: '',
          challenges: '',
          nextSteps: '',
          kpiId: '',
          kpiName: '',
          isNew: true,
          subTasks: []
        }]);
        setSelectedTaskIndex(0);
        setHasSubmittedTaskThisWeek(false);

        showNotification(`No tasks found for ${weekDate}. Create a new task.`, 'info');
      }
    } catch (error) {
      console.error('❌ Error loading existing tasks:', error);
      showNotification('Failed to load tasks for this week', 'error');
    }
  };

  const handleClearObjective = async () => {
    if (selectedObjective) {
      await deleteDraft(selectedObjective.id, selectedWeek);
      setObjectiveDrafts(prev => ({
        ...prev,
        [selectedObjective.id]: false
      }));
    }

    setSelectedObjective(null);
    setHasSubmittedTaskThisWeek(false);
    setHasUnsavedChanges(false);
    setKpiEnforcementActive(false); // Reset enforcement
    setTasks([{
      id: Date.now().toString(),
      taskName: "",
      taskDescription: "",
      progressPercent: 0,
      status: "",
      achievements: "",
      challenges: "",
      nextSteps: "",
      kpiId: "",
      kpiName: "",
      isNew: true
    }]);
    setSelectedTaskIndex(0);
  };

  useEffect(() => {
    const fetchUser = async () => {
      const userInfo = await getCurrentUserWithRole(engine);
      setCurrentUser(userInfo);
    };

    fetchUser();
  }, [engine]);

  // Fetch objectives
  useEffect(() => {
    const fetchObjectives = async () => {
      if (!currentUser) return;

      try {
        setIsLoading(true);

        const response = await engine.query({
          objectives: {
            resource: 'events',
            params: {
              program: PROGRAM_ID,
              programStage: 'VIBtpvkpHFR',
              fields: 'event,orgUnit,orgUnitName,eventDate,dataValues[dataElement,value],trackedEntityInstance,createdBy,createdByUserInfo,storedBy,lastUpdatedByUserInfo',
              pageSize: 1000,
              order: 'eventDate:desc'
            }
          }
        });

        const processObjectives = async () => {
          const processedObjectives = [];

          console.log('Current user:', currentUser);

          for (const event of response.objectives?.events || []) {
            const dataValues = event.dataValues || [];

            const usernameRaw =
              event.createdByUserInfo?.username ||
              event.storedBy ||
              event.lastUpdatedByUserInfo?.username ||
              null;

            const creatorUsername = usernameRaw ? usernameRaw.toLowerCase().trim() : '';

            console.log('Objective creator username:', creatorUsername, 'Full event:', event);

            const isVisible = shouldShowObjective(
              currentUser.roleId,
              currentUser.username,
              creatorUsername
            );

            console.log('Is visible:', isVisible, 'Current user:', currentUser.username, 'Creator:', creatorUsername);

            if (isVisible) {
              processedObjectives.push({
                id: event.event,
                name: dataValues.find(dv => dv.dataElement === 'EuN5ztFnXsQ')?.value || 'Unnamed Objective',
                description: dataValues.find(dv => dv.dataElement === 'nWqW5BrcgP1')?.value || '',
                orgUnit: event.orgUnit,
                orgUnitName: event.orgUnitName,
                trackedEntityInstance: event.trackedEntityInstance,
                startDate: event.eventDate,
                createdBy: creatorUsername
              });
            }
          }

          console.log('Final processed objectives:', processedObjectives);
          return processedObjectives;
        };

        const visibleObjectives = await processObjectives();
        setObjectives(visibleObjectives);

      } catch (error) {
        console.error('Error fetching objectives:', error);
        showNotification('Failed to load objectives', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchObjectives();
  }, [currentUser, engine]);

  // Load status options
  useEffect(() => {
    const loadOptions = async () => {
      const options = await fetchStatusOptions();
      const formattedOptions = options.map(opt => ({
        value: opt.code,
        label: opt.name
      }));
      setStatusOptions(formattedOptions);
    };
    loadOptions();
  }, []);

  // Load existing tasks when objective is selected
  useEffect(() => {
    if (!selectedObjective) {
      setExistingTasks([]);
      return;
    }

    const loadExistingTasks = async (objectiveId) => {
      try {
        const response = await engine.query({
          tasks: {
            resource: 'events',
            params: {
              program: PROGRAM_ID,
              programStage: TASK_PROGRAM_STAGE_ID,
              filter: [`plJovUlxfQ0:eq:${objectiveId}`],
              fields: 'event,eventDate,orgUnitName,dataValues[dataElement,value]',
              pageSize: 1000,
              order: `${TASK_DATAELEMENT_MAPPING.selectedWeek}:desc`
            }
          }
        });

        const existingTasksData = response.tasks?.events?.map(event => {
          const dataValues = event.dataValues || [];

          // Parse subtasks
          const subTasksValue = dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.subTasks)?.value;
          let subTasks = [];
          try {
            subTasks = subTasksValue ? JSON.parse(subTasksValue) : [];
          } catch (error) {
            console.warn('Failed to parse subtasks JSON for existing task:', error);
            subTasks = [];
          }

          return {
            id: event.event, // Use database event ID as task ID
            eventId: event.event, // Store event ID
            selectedWeek: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.selectedWeek)?.value || 'Unknown date',
            progressPercent: parseInt(dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.progressPercent)?.value) || 0,
            status: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.status)?.value || '',
            challenges: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.challenges)?.value || '',
            nextSteps: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.nextSteps)?.value || '',
            achievements: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.achievements)?.value || '',
            taskName: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.taskName)?.value || '',
            taskDescription: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.taskDescription)?.value || '',
            kpiId: dataValues.find(dv => dv.dataElement === TASK_DATAELEMENT_MAPPING.kpiId)?.value || '',
            subTasks: subTasks,
            isNew: false // Mark as existing database event
          };
        }) || [];

        setExistingTasks(existingTasksData);
        return existingTasksData;

      } catch (error) {
        console.error('Error loading existing tasks:', error);
        return [];
      }
    };

    loadExistingTasks(selectedObjective.id);
  }, [selectedObjective, engine]);

  // Pre-check which objectives already have tasks for the current week
  useEffect(() => {
    const checkObjectivesForTasksAndDrafts = async () => {
      const tasksMap = {};
      const draftsMap = {};

      for (const objective of objectives) {
        try {
          // Check for submitted tasks
          const hasTask = await checkExistingTaskThisWeek(objective.id, selectedWeek);
          tasksMap[objective.id] = hasTask;

          // Check for drafts (only if no submitted task exists)
          if (!hasTask) {
            const hasDraft = await checkObjectiveHasDraft(objective.id, selectedWeek);
            draftsMap[objective.id] = hasDraft;
          } else {
            draftsMap[objective.id] = false;
          }
        } catch (error) {
          console.error(`Error checking task/draft for objective ${objective.id}:`, error);
          tasksMap[objective.id] = false;
          draftsMap[objective.id] = false;
        }
      }

      setObjectiveTasks(tasksMap);
      setObjectiveDrafts(draftsMap);
    };

    if (objectives.length > 0) {
      checkObjectivesForTasksAndDrafts();
    }
  }, [objectives, selectedWeek]);

  // Delay autosave until meaningful content exists (prevents duplicate empty tasks)
useEffect(() => {
  // CRITICAL: Only trigger autosave when there's ACTUAL WORK (not just empty form)
  const hasMeaningfulContent = tasks.some(task => {
    // Must have valid task name first
    const hasValidTaskName = task.taskName?.trim() && task.taskName !== 'Unnamed Task';
    if (!hasValidTaskName) return false;
    
    // CHECK: At least one activity/subtask has meaningful content
    const hasActivityContent = task.subTasks?.some(sub => 
      (sub.description?.trim() && sub.description.trim().length > 5) || // Min 5 chars
      (sub.progressPercent > 0) // Progress updated
    );
    
    // ✅ SECONDARY CHECK: Other substantive fields filled (fallback)
    const hasOtherContent = 
      (task.achievements?.trim() && task.achievements.trim().length > 10) ||
      (task.challenges?.trim() && task.challenges.trim().length > 10);
    
    return hasActivityContent || hasOtherContent;
  });

  // ONLY autosave when:
  // 1. Meaningful content exists (activities filled OR other fields substantively filled)
  // 2. User made changes (dirty state)
  // 3. Valid objective selected
  // 4. Week isn't locked
  // 5. NO existing submitted task for current week (prevents duplicates)
  if (
    hasMeaningfulContent && 
    hasUnsavedChanges && 
    selectedObjective && 
    !isWeekLocked(selectedWeek) && 
    !hasSubmittedTaskThisWeek
  ) {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    autoSaveTimeoutRef.current = setTimeout(() => {
      console.log('✅ AUTOSAVE TRIGGERED: Meaningful content detected');
      saveDraft(tasks, selectedObjective, selectedWeek);
    }, AUTO_SAVE_DELAY);
    
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }
  
  // Optional: Log why autosave was skipped for debugging
  if (hasUnsavedChanges && selectedObjective && !isWeekLocked(selectedWeek)) {
    console.log('⏸️ AUTOSAVE SKIPPED - No meaningful content yet:', {
      hasValidTaskName: tasks.some(t => t.taskName?.trim() && t.taskName !== 'Unnamed Task'),
      hasActivityContent: tasks.some(t => t.subTasks?.some(s => s.description?.trim() || s.progressPercent > 0)),
      hasOtherContent: tasks.some(t => 
        (t.achievements?.trim()?.length > 10) || 
        (t.challenges?.trim()?.length > 10)
      ),
      hasSubmittedTaskThisWeek,
      isWeekLocked: isWeekLocked(selectedWeek)
    });
  }
}, [tasks, hasUnsavedChanges, selectedObjective, selectedWeek, hasSubmittedTaskThisWeek]);
  // Stable subtask callbacks
  const handleUpdateSubTask = useCallback((subTaskIndex, updates) => {
    // IMMEDIATELY update local UI state — no debounce!
    setTasks(prev => {
      const newTasks = [...prev];
      const currentSubTasks = [...(newTasks[selectedTaskIndex]?.subTasks || [])];
      const updatedSubTask = { ...currentSubTasks[subTaskIndex], ...updates };
      currentSubTasks[subTaskIndex] = updatedSubTask;

      const updatedTask = { ...newTasks[selectedTaskIndex], subTasks: currentSubTasks };
      updatedTask.progressPercent = calculateTaskProgress(currentSubTasks);

      newTasks[selectedTaskIndex] = updatedTask;
      return newTasks;
    });

    if (!isFormDisabled && selectedObjective) {
      setHasUnsavedChanges(true);
    }
  }, [selectedTaskIndex, selectedObjective, hasSubmittedTaskThisWeek, isFormDisabled]);

  const handleRemoveSubTask = useCallback((subTaskIndex) => {
    setTasks(prev => {
      const newTasks = [...prev];
      const updatedSubTasks = (newTasks[selectedTaskIndex]?.subTasks || []).filter((_, i) => i !== subTaskIndex);
      const updatedTask = { ...newTasks[selectedTaskIndex], subTasks: updatedSubTasks };
      updatedTask.progressPercent = calculateTaskProgress(updatedSubTasks);
      newTasks[selectedTaskIndex] = updatedTask;
      setHasUnsavedChanges(true);
      return newTasks;
    });
  }, [selectedTaskIndex]); // ✅ Stable dependency

  // Also stabilize task-level update if used elsewhere
  const updateCurrentTask = useCallback((updates) => {
    setTasks(prev => {
      const newTasks = [...prev];
      const updatedTask = { ...newTasks[selectedTaskIndex], ...updates };
      if (updates.subTasks !== undefined) {
        updatedTask.progressPercent = calculateTaskProgress(updatedTask.subTasks);
      }
      newTasks[selectedTaskIndex] = updatedTask;
      return newTasks;
    });

    // ✅ ALWAYS mark as unsaved when user makes changes (unless form is disabled)
    if (!isFormDisabled) {
      setHasUnsavedChanges(true);
    }
  }, [selectedTaskIndex, isFormDisabled]);

  // Add KPI progress calculation
  const calculateKPIProgress = () => {
    const kpiProgress = {};

    // Combine current tasks and existing DHIS2 events
    const allTasks = [
      ...tasks.filter(task => task.kpiId && task.progressPercent > 0),
      ...existingTasks.filter(task => task.kpiId && task.progressPercent > 0)
    ];

    allTasks.forEach(task => {
      if (task.kpiId && task.progressPercent > 0) {
        if (!kpiProgress[task.kpiId]) {
          kpiProgress[task.kpiId] = {
            kpiName: task.kpiName || 'Unknown KPI',
            totalProgress: 0,
            taskCount: 0,
            tasks: []
          };
        }

        kpiProgress[task.kpiId].totalProgress += task.progressPercent;
        kpiProgress[task.kpiId].taskCount += 1;
        kpiProgress[task.kpiId].tasks.push({
          taskName: task.taskName,
          progress: task.progressPercent,
          isSubmitted: !task.isNew
        });
      }
    });

    // Calculate average progress for each KPI
    Object.keys(kpiProgress).forEach(kpiId => {
      kpiProgress[kpiId].averageProgress =
        kpiProgress[kpiId].totalProgress / kpiProgress[kpiId].taskCount;
    });

    return kpiProgress;
  };

  // KPI Progress Display Component
  const KPIProgressDisplay = () => {
    const kpiProgress = calculateKPIProgress();

    if (Object.keys(kpiProgress).length === 0) {
      return null;
    }

    return (
      <div style={{
        marginTop: '24px',
        padding: '16px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        <h4 style={{ margin: '0 0 16px 0', color: '#2c3e50' }}>
          KPI Progress Summary
        </h4>
        <div style={{ display: 'grid', gap: '12px' }}>
          {Object.entries(kpiProgress).map(([kpiId, data]) => (
            <div key={kpiId} style={{
              padding: '12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              border: '1px solid #ddd'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <strong>{data.kpiName}</strong>
                <span style={{
                  padding: '4px 8px',
                  backgroundColor: '#2980b9',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '0.8rem'
                }}>
                  Average: {data.averageProgress.toFixed(1)}%
                </span>
              </div>
              <div style={{ fontSize: '0.8rem', color: '#666' }}>
                {data.taskCount} task(s) contributing to this KPI
              </div>
              <div style={{ marginTop: '8px' }}>
                {data.tasks.map((task, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.7rem',
                    padding: '2px 0'
                  }}>
                    <span>{task.taskName}</span>
                    <span>{task.progress}%</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleViewTask = (task) => {
    setSelectedTask(task);
    setIsTaskModalOpen(true);
  };

  const handleCloseTaskModal = () => {
    setIsTaskModalOpen(false);
    setSelectedTask(null);
  };


  const disabledStyle = {
    backgroundColor: '#f8f9fa',
    cursor: 'not-allowed',
    opacity: 0.7
  };

  const renderFormField = (label, children, required = true) => (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ fontWeight: '400', marginBottom: '2px' }}>
        {label} {required && <span style={{ color: 'red' }}>*</span>}
      </div>
      {children}
    </div>
  );


  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px'
      }}>
        <CircularLoader />
        <span style={{ marginLeft: '12px' }}>Loading objectives...</span>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div
        style={{
          maxWidth: 1200,
          margin: '2rem auto',
          padding: '24px',
          backgroundColor: '#ffffffff',
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        }}
      >

        <h3
          style={{
            textAlign: 'center',
            color: '#297934ff',
            marginBottom: '20px',
            paddingBottom: '10px',
            borderBottom: '2px solid #dfe3f7ff',
          }}
        >
          Weekly Tasks Capture
        </h3>

        {notification && (
          <div
            style={{
              position: 'fixed',
              bottom: '20px',
              right: '400px',
              zIndex: 10000,
              width: '350px',
            }}
          >
            <AlertBar
              {...(notification.type === 'success'
                ? { success: true }
                : { warning: true })}
              onHidden={() => setNotification(null)}
              style={{ marginBottom: '16px' }}
            >
              {notification.message}
            </AlertBar>
          </div>
        )}

        {/* Two-column layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '24px' }}>

          {selectedObjective && (
            <div style={{ marginBottom: '20px' }}>
              {renderFormField(
                'Select Week to View/Edit',
                <SingleSelect
                  selected={selectedWeek}
                  onChange={({ selected }) => {
                    setWeekEnding(selected);
                    loadExistingTasksForWeek(selectedObjective.id, selected);
                  }}
                >
                  {allWeeks.map((week) => (
                    <SingleSelectOption
                      key={week}
                      value={week}
                      label={`Week Ending ${week} ${isViewOnlyWeek(week)
                          ? '(Edit Existing Only)'
                          : '(Add/Edit Tasks)'
                        }`}
                    />
                  ))}
                </SingleSelect>,
                false
              )}
              {isSpecificWeekLocked(selectedWeek) && (
                <AlertBar warning style={{ marginTop: '8px' }}>
                  This week is locked for editing. You can only view tasks from this week.
                </AlertBar>
              )}
            </div>
          )}

          {/* Step 1: Select Objective */}
          {currentStep === 'SELECT_OBJECTIVE' && (
            <div>
              <h3 style={{ marginBottom: '16px', color: '#2c3e50' }}>
                Your Objectives
              </h3>
      
              {/* Objective Selection Zone */}
              <ObjectiveSelectionDisplay
                selectedObjective={selectedObjective}
                onClear={handleClearObjective}
                hasTaskThisWeek={hasSubmittedTaskThisWeek}
              />

              {/* Search Box */}
             {renderFormField(
                  'Search Objective',
                  <div style={{ 
                    position: 'relative', 
                    display: 'block',
                    width: '100%' 
                  }}>
                    <Input
                      type="text"
                      placeholder="Search objectives..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      style={{ 
                        paddingLeft: '3.5rem',  // 56px - forces space inside DHIS2 input
                        paddingRight: '1rem'
                      }}
                    />
                    <IconSearch24
                      style={{
                        position: 'absolute',
                        left: '1rem',        // 16px from container edge
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#666',
                        pointerEvents: 'none',
                        zIndex: 10,
                        width: '20px',
                        height: '20px'
                      }}
                    />
                  </div>,
                  false
                )}
            {/* Objectives List */}
              <div
                style={{
                  maxHeight: '500px',
                  overflowY: 'auto',
                  padding: '12px',
                  border: '1px solid #aeb8f1ff',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  backgroundColor: '#e9f7efff',
                }}
              >
                {filteredObjectives.length === 0 ? (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '40px',
                      color: '#e79506ff',
                    }}
                  >
                    {objectives.length === 0
                      ? 'No objectives found. Create an objective first.'
                      : 'No objectives match your search'}
                  </div>
                ) : (
                  filteredObjectives.map((objective) => (
                    <DraggableObjectiveCard
                      key={objective.id}
                      objective={objective}
                      isSelected={selectedObjective?.id === objective.id}
                      onSelect={(obj) => {
                        handleObjectiveSelect(obj);
                        setCurrentStep('SELECT_KPI');
                      }}
                      hasSubmittedTaskThisWeek={objectiveTasks[objective.id] || false}
                      hasDraftThisWeek={objectiveDrafts[objective.id] || false}
                    />
                  ))
                )}
              </div>
            </div>
          )}

          {/* Step 2: Select KPI */}
          {currentStep === 'SELECT_KPI' && selectedObjective && (
            <div>
              <div
                style={{
                  marginBottom: '24px',
                  color: '#0e6c05ff',
                  padding: '16px',
                  backgroundColor: '#caf5bfff',
                  borderRadius: '8px',
                }}
              >
                <strong>Selected Objective:</strong> {selectedObjective.name}
              </div>


              <div style={{ position: 'relative', zIndex: 1001 }}>
                <button
                  onClick={handleBackToObjective} 
                  style={{
                    marginBottom: '16px',
                    backgroundColor: '#053e70ff',
                    color: '#f6f7f9ff',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    border: 'none',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                    cursor: 'pointer',
                  }}
                >
                  ← Back to Tasks Main Page
                </button>
              </div>

              <h4 style={{ color: '#bd4e09ff', marginBottom: '12px' }}>
                Click to link KPI to this task
              </h4>

              {kpis.length > 0 ? (
                <div
                  style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    display: 'grid',
                    backgroundColor: 'rgb(229, 225, 223)',
                    borderRadius: '8px',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '18px',

                  }}
                >
                  {kpis.map((kpi) => (
                    <DraggableKPICard
                      key={kpi.id}
                      kpi={kpi}
                      isSelected={selectedKPI?.eventId === kpi.eventId}
                      onSelect={(kpi) => {
                        setSelectedKPI(kpi);
                        updateCurrentTask({
                          kpiId: kpi.eventId,
                          kpiName: kpi.name,
                        });
                        setCurrentStep('FILL_TASK');
                      }}
                    />
                  ))}
                </div>
              ) : (
                <p>No KPIs found for this objective.</p>
              )}
            </div>
          )}

          {/* Step 3: Fill Task Form (Simplified) */}
          {currentStep === 'FILL_TASK' && selectedObjective && selectedKPI && (
            <>
              {isPastWeek(selectedWeek) && (
                <AlertBar
                  warning
                  style={{
                    marginBottom: '16px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107'
                  }}
                >
                  <strong>⚠️ Past Week Editing:</strong> You can only edit existing tasks from this week.
                  Adding new tasks or activities is not allowed for past weeks.
                </AlertBar>
              )}

              <div
                style={{
                  marginBottom: '24px',
                  color: '#0e6c05ff',
                  padding: '16px',
                  backgroundColor: '#caf5bfff',
                  borderRadius: '8px',
                }}
              >
                <div>
                  <strong>Objective:</strong> {selectedObjective.name}
                </div>
                <div>
                  <strong>KPI:</strong> {selectedKPI.name}
                </div>
              </div>

              <AutoSaveIndicator
                isAutoSaving={isAutoSaving}
                hasUnsavedChanges={hasUnsavedChanges}
                lastAutoSaved={lastAutoSaved}
                isFormDisabled={isFormDisabled}
                customMessage={isViewOnlyWeek(selectedWeek)
                  ? "Editing existing task from past week"
                  : undefined}
              />

              {/* ===== TASK NAVIGATOR FOR MULTIPLE TASKS ===== */}
              {hasSubmittedTaskThisWeek && !showTaskForm && (
                <div style={{
                  marginBottom: '24px',
                  padding: '16px',
                  border: '2px solid #2196f3',
                  borderRadius: '12px',
                  backgroundColor: '#e3f2fd',
                  boxShadow: '0 4px 12px rgba(33, 150, 243, 0.15)'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '16px',
                    paddingBottom: '12px',
                    borderBottom: '1px solid #bbdefb'
                  }}>
                    <h4 style={{ 
                      margin: 0, 
                      color: '#0d47a1', 
                      fontSize: '1.3rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <span style={{ fontSize: '1.8rem' }}>📋</span>
                      Tasks for Week Ending {selectedWeek} ({tasks.length})
                    </h4>
                    <span style={{
                      backgroundColor: '#1565c0',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '0.9rem',
                      fontWeight: 'bold'
                    }}>
                      {tasks.filter(t => t.subTasks?.length > 0).length} with activities
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {tasks.map((task, index) => {
                      const hasActivities = task.subTasks?.length > 0;
                      const isActive = selectedTaskIndex === index;
                      
                      return (
                        <div
                          key={task.id || index}
                          onClick={() => {
                                setSelectedTaskIndex(index);
                                setShowTaskForm(true); 
                              }}
                          style={{
                            padding: '16px',
                            border: isActive 
                              ? '2px solid #0d47a1' 
                              : hasActivities 
                                ? '1px solid #90caf9' 
                                : '1px dashed #bbdefb',
                            borderRadius: '10px',
                            backgroundColor: isActive ? '#bbdefb' : 'white',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            boxShadow: isActive ? '0 3px 10px rgba(13, 71, 161, 0.2)' : 'none',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          {/* Visual indicator bar */}
                          <div style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: '6px',
                            backgroundColor: hasActivities 
                              ? (isActive ? '#0d47a1' : '#2196f3') 
                              : '#bdbdbd'
                          }} />
                          
                          <div style={{ flex: 1, paddingLeft: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
                              <div style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                backgroundColor: isActive ? '#0d47a1' : '#e3f2fd',
                                color: isActive ? 'white' : '#0d47a1',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                flexShrink: 0,
                                border: isActive ? '2px solid white' : '2px solid #0d47a1'
                              }}>
                                {index + 1}
                              </div>
                              <strong style={{ 
                                color: isActive ? '#0a2e5e' : '#2c3e50',
                                fontSize: '1.1rem',
                                maxWidth: '60%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {task.taskName || `Task ${index + 1}`}
                              </strong>
                            </div>
                            
                            <div style={{ display: 'flex', gap: '20px', fontSize: '0.95rem', color: '#424242' }}>
                              <span>🎯 Activities: <strong>{task.subTasks?.length || 0}</strong></span>
                              <span>📈 Progress: <strong>{task.progressPercent || 0}%</strong></span>
                              {task.kpiName && (
                                <span style={{ maxWidth: '40%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  KPI: <em>{task.kpiName}</em>
                                </span>
                              )}
                            </div>
                            
                            {!hasActivities && (
                              <div style={{ 
                                marginTop: '8px', 
                                padding: '6px 12px', 
                                backgroundColor: '#fff8e1', 
                                borderRadius: '6px', 
                                fontSize: '0.85rem',
                                color: '#5d4037',
                                borderLeft: '3px solid #ffc107'
                              }}>
                                ⚠️ No activities added yet
                              </div>
                            )}
                          </div>
                          <Button
                            small
                            primary
                            style={{ 
                              minWidth: '90px',
                              backgroundColor: isActive ? '#0d47a1' : 'white',
                              color: isActive ? 'white' : '#0d47a1',
                              border: `1px solid ${isActive ? '#0a2e5e' : '#2196f3'}`,
                              fontWeight: 'bold',
                              boxShadow: isActive ? '0 2px 6px rgba(13, 71, 161, 0.3)' : 'none'
                            }}
                          >
                          {isActive ? 'ACTIVE' : 'VIEW'}
                          </Button>
                        </div>
                      );
                    })}
                  </div>       
                                  <div style={{ 
                    marginTop: '16px', 
                    padding: '12px', 
                    backgroundColor: '#bbdefb', 
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    color: '#0a2e5e',
                    borderLeft: '3px solid #0d47a1'
                  }}>
                    💡 <strong>Tip:</strong> Click any task above to view/edit its activities. Tasks with activities are highlighted in blue.
                  </div>
                </div>
              )}

              {showTaskForm && (
              <>
             
              {/* Task Name - Edit existing but prevent new additions in past weeks */}
              {renderFormField(
                'Task Name',
                <Input
                  value={currentTask.taskName}
                  onChange={({ value }) => {
                    // Only allow editing if not a past week OR if task already has a name
                    if (!isPastWeek(selectedWeek) || currentTask.taskName) {
                      updateCurrentTask({ taskName: value })
                    }
                  }}
                  placeholder={isPastWeek(selectedWeek) && !currentTask.taskName ? "Cannot add new tasks in past weeks" : "Task name"}
                  disabled={isFormDisabled || (isPastWeek(selectedWeek) && !currentTask.taskName)}
                  required
                />
              )}

              {/* Task Description */}
              {renderFormField(
                'Task Description',
                <TextArea
                  value={currentTask.taskDescription}
                  onChange={({ value }) =>
                    updateCurrentTask({ taskDescription: value })
                  }
                  placeholder="Task description"
                  rows={3}
                  disabled={isFormDisabled}
                  required
                />
              )}


              {/* Activities Section */}
              <div style={{ marginBottom: '20px', border: '1px solid #ddd', borderRadius: '8px', padding: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ margin: 0, color: '#2c3e50' }}>
                    Activities ({currentTask.subTasks?.length || 0})
                  </h4>

                  {/* ✅ UPDATED: Only show button if NOT a past week AND form is not disabled */}
                  {!isPastWeek(selectedWeek) && !isFormDisabled && (
                    <Button
                      small
                      primary
                      onClick={() => {
                        updateCurrentTask({
                          subTasks: [...(currentTask.subTasks || []), {
                            id: Date.now().toString(),
                            name: '',
                            description: '',
                            progressPercent: 0,
                            completed: false
                          }]
                        });
                      }}
                    >
                      + Add Activity
                    </Button>
                  )}

                  {/* ✅ Show message when button is hidden */}
                  {isPastWeek(selectedWeek) && (
                    <span style={{ color: '#666', fontSize: '0.9rem', fontStyle: 'italic' }}>
                      (No new activities can be added to past weeks)
                    </span>
                  )}
                </div>

                {/* SubTasks */}
                {currentTask.subTasks?.map((activity, index) => (
                  <SubTask
                    key={activity.id}
                    id={activity.id}
                    name=""
                    description={activity.description}
                    progressPercent={activity.progressPercent}
                    completed={activity.completed}
                    index={index}
                    onUpdate={handleUpdateSubTask}
                    onRemove={handleRemoveSubTask}
                    isFormDisabled={isFormDisabled}
                  />
                ))}

                {currentTask.subTasks?.length === 0 && (
                  <p style={{ color: '#666', textAlign: 'center', fontStyle: 'italic' }}>
                    {isViewOnlyWeek(selectedWeek)
                      ? 'No activities exist for this past week'
                      : 'No activities added yet. Click "+ Add Activity" to get started.'
                    }
                  </p>
                )}
              </div>

              {/* Progress % */}
              <div style={{ marginBottom: '20px' }}>
                {renderFormField('Progress (%)', (
                  <div style={{ position: 'relative' }}>
                    {currentTask.subTasks?.length > 0 ? (
                      <div style={{
                        padding: '16px 20px',
                        backgroundColor: '#f8f9fa',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: '#212529',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        minHeight: '48px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {currentTask.progressPercent}%
                      </div>
                    ) : (
                      <div style={{ position: 'relative' }}>
                        <Input
                          type="number"
                          value={currentTask.progressPercent}
                          style={{
                            borderColor: getProgressColor(currentTask.progressPercent),
                            borderWidth: '2px',
                            borderRadius: '4px'
                          }}
                        />
                        <small style={{ color: '#666', fontStyle: 'italic' }}>
                          Set manually (Activities auto-calculate)
                        </small>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Achievements */}
              {renderFormField(
                'Task Achievements / Milestone',
                <TextArea
                  value={currentTask.achievements}
                  onChange={({ value }) =>
                    updateCurrentTask({ achievements: value })
                  }
                  placeholder="What was accomplished?"
                  rows={3}
                  disabled={isFormDisabled}
                />
              )}

              {/* Challenges */}
              {renderFormField(
                'Task Challenges / Risks',
                <TextArea
                  value={currentTask.challenges}
                  onChange={({ value }) =>
                    updateCurrentTask({ challenges: value })
                  }
                  placeholder="What obstacles were faced?"
                  rows={3}
                  disabled={isFormDisabled}
                />
              )}
               {/* Back button when viewing existing tasks */}
                {hasSubmittedTaskThisWeek && (
                  <button 
                    secondary 
                    onClick={() => setShowTaskForm(false)}
                    style={{marginBottom: '16px',
                    backgroundColor: '#053e70ff',
                    color: '#f6f7f9ff',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    border: 'none',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                    cursor: 'pointer', }}
                  >
                    ← Back to Tasks List
                  </button>
                )}
              {/* Navigation */}
              <div style={{ position: 'relative', zIndex: 1001 }}>
                <button
                  onClick={() => setCurrentStep('SELECT_KPI')}
                  style={{
                    marginBottom: '16px',
                    backgroundColor: '#053e70ff',
                    color: '#f6f7f9ff',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    border: 'none',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                    cursor: 'pointer',
                  }}
                >
                  ← Back to Key Performance Indicators
                </button>
                {onCancel && <Button warning onClick={onCancel}>Cancel</Button>}
              </div>  
            </>
          )}

          {/* Empty State */}
          {currentStep === 'SELECT_OBJECTIVE' &&
            !selectedObjective &&
            objectives.length > 0 && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#bdc3c7',
                  backgroundColor: '#edf1e3ff',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                  border: '1px solid #ddd',
                }}
              >
                <h3 style={{ color: '#0b5109ff' }}>Select an Objective</h3>
                <p style={{ fontSize: '1.2rem', marginTop: '8px', color: '#053006ff' }}>
                  Your progress will be automatically saved as you type
                </p>
              </div>
            )}
               </>
             )}
        </div>
        
        {/* Task Detail Modal */}
        {isTaskModalOpen && (
          <TaskDetailModal
            task={selectedTask}
            statusOptions={statusOptions}
            onClose={handleCloseTaskModal}
          />
        )}
      </div>
    </DndProvider>
  );
}

export default WeeklyProgressTracker;